import React, { useMemo } from 'react';
import { useTheme } from 'styled-components';
import { ResponsiveContainer, AreaChart as RechartAreaChart, CartesianGrid, XAxis, YAxis, Tooltip as TooltipRecharts, Area, Legend } from 'recharts';
import { tooltipFormatter as defaultTooltipFormatter } from '../column-chart/utils';
import { Legend as CustomLegend, Tick as CustomTick } from '../components';
import { getColorShade } from '../../../../src/styled/utils';
import { getColorByIndex } from '../utils';
/**
 * Component for rendering an area chart using Recharts.
 *
 * @param {AreaChartPropsT} props - The properties for the AreaChart component.
 * @param {string[]} props.dataKeys - The keys for the data points in the chart.
 * @param {string} props.dataKeyXAxis - The key for the X-axis data.
 * @param {Function} [props.onMouseMove] - Function to call on mouse move events.
 * @param {Function} [props.onMouseLeave] - Function to call on mouse leave events.
 * @param {RGBColor[]} [props.chartColors] - Colors to use for the chart lines and areas.
 * @param {LineType} [props.lineType='monotone'] - The type of the line for the area chart.
 * @param {TickFormatterFunction} [props.tickXFormatter] - Formatter function for the X-axis ticks.
 * @param {Function} [props.tickYFormatter] - Formatter function for the Y-axis ticks.
 * @param {TooltipFormatter} [props.tooltipFormatter=defaultTooltipFormatter] - Formatter function for the tooltip.
 * @returns {JSX.Element} The rendered AreaChart component.
 */
export const AreaChart = ({ dataKeys, dataKeyXAxis, onMouseMove, onMouseLeave, chartColors: chartColorsProps, lineType = 'monotone', tickXFormatter, tickYFormatter, tooltipFormatter = defaultTooltipFormatter, ...props }) => {
    const theme = useTheme();
    const { chartColors: defaultChartColors, sizeUnit } = theme;
    const chartColors = chartColorsProps && chartColorsProps.length ? chartColorsProps : defaultChartColors;
    const cartesianGridStroke = useMemo(() => getColorShade('surface', 'outline')({ theme }), [theme]);
    const handleMouseMove = (nextState, e) => {
        if (onMouseMove) {
            onMouseMove(nextState, e);
        }
    };
    const handleMouseLeave = (nextState, e) => {
        if (onMouseLeave) {
            onMouseLeave(nextState, e);
        }
    };
    const renderAreas = () => {
        return dataKeys.map((dataKey, index) => {
            const currentColor = getColorByIndex(chartColors, index);
            return (React.createElement(Area, { key: dataKey, dataKey: dataKey, type: lineType, stroke: currentColor, fill: currentColor, strokeWidth: 2 }));
        });
    };
    return (React.createElement(ResponsiveContainer, null,
        React.createElement(RechartAreaChart, { ...props, onMouseMove: handleMouseMove, onMouseLeave: handleMouseLeave },
            React.createElement(CartesianGrid, { vertical: false, strokeWidth: sizeUnit * 0.125, fill: cartesianGridStroke }),
            React.createElement(XAxis, { dataKey: dataKeyXAxis, axisLine: false, tickLine: false, tick: React.createElement(CustomTick, { tickFormatter: tickXFormatter }), tickFormatter: tickXFormatter }),
            React.createElement(YAxis, { axisLine: false, tickLine: false, tickMargin: 20, tick: React.createElement(CustomTick, { tickFormatter: tickYFormatter }), tickFormatter: tickYFormatter }),
            React.createElement(TooltipRecharts, { formatter: tooltipFormatter }),
            React.createElement(Legend, { content: React.createElement(CustomLegend, null) }),
            renderAreas())));
};
